/**
 * 框架通道配置页写操作的错误 → 提示映射（A8 §10.4 + `01-通用约定/21-Admin错误码段.md` §2.5）。
 *
 * 只按 `code` 与 `details` 的结构化字段分支（`reason` / `issues[].code` / `errors[].field`），不按 message 文案。
 * 每条提示带 `placement`：需要运营动手补正的留在行内（`inline`），一次性结果用 toast；
 * 环境级问题（凭据加密载体缺失、本环境未声明 mode）单独标 `environmental`，与运营自己能补的区分开。
 */

import {
  type PaymentProviderChannel,
  PaymentProviderChannelSchema,
  type PaymentProviderMode,
} from '@novel-admin/shared';
import { AdminApiError } from '~/api';
import { AdminNetworkError, UnexpectedAdminApiResponseError } from '~/api/errors';
import { detailsReasonIs } from '~/lib/details-reason';
import {
  type ProviderChannelCopy,
  providerChannelCopy,
  providerDisplayName,
} from './provider-channel-copy';

export type ProviderErrorScene =
  | 'save'
  | 'test'
  | 'activate'
  | 'deactivate'
  | 'register'
  | 'endpoint-state'
  | 'material';

export type ProviderNoticeKind =
  | 'material-missing'
  | 'config-incomplete'
  | 'connection-unverified'
  | 'route-missing'
  | 'generic';

export type ProviderNotice = {
  kind: ProviderNoticeKind;
  /** info = 环境级问题（需运维处理，与运营能补的 warn / neg 区分） */
  tone: 'neg' | 'warn' | 'info';
  title: string;
  body?: string;
  /** 机读码摘要，如 `PAY_PROVIDER_NOT_READY · MODE_MISMATCH`；非 API 错误为空串 */
  code: string;
  placement: 'inline' | 'toast';
  /** 环境级：页面上重填无法解决，指向运维 */
  environmental?: boolean;
  /** 写入后需要重拉配置列表（状态可能已变或对象已不存在） */
  refresh?: boolean;
  /** 幂等键与内容不一致：调用方须换新键 */
  rotateIdempotencyKey?: boolean;
  /** `details.app_id`（配置形态的错误）；轮转失败时用来点名受影响 App */
  appId?: string;
};

/**
 * 写请求结果是否未知：中止类错误带 `serverStateUnknown`；断网与非 envelope 响应同样拿不到服务端结论。
 * 这几类都继承 `AdminApiError`，必须在按 code 分支之前判。
 */
export function isAmbiguousWriteError(error: unknown): boolean {
  if (error instanceof AdminNetworkError || error instanceof UnexpectedAdminApiResponseError) {
    return true;
  }
  return (
    !!error &&
    typeof error === 'object' &&
    (error as { serverStateUnknown?: unknown }).serverStateUnknown === true
  );
}

export type ProviderErrorContext = {
  /** 本环境 mode：值 = 已声明；null = 环境未声明（环境级问题）；undefined = 能力声明未读到、未知 */
  deploymentMode?: PaymentProviderMode | null;
  /** app_id → 展示名；未知 App 回退 id */
  appName?: (appId: string) => string;
  /**
   * 本页通道：决定供应商名与验签材料称呼。缺省时取错误 `details.channel`；两者都没有时用中性文案，
   * 不冒用某一家的名字。
   */
  channel?: PaymentProviderChannel;
};

function issueCodes(details: Record<string, unknown>): string[] {
  const issues = details.issues;
  if (!Array.isArray(issues)) return [];
  return issues
    .map((i) => (i && typeof i === 'object' ? (i as { code?: unknown }).code : undefined))
    .filter((c): c is string => typeof c === 'string');
}

function stringDetail(details: Record<string, unknown>, key: string): string | undefined {
  const value = details[key];
  return typeof value === 'string' ? value : undefined;
}

const SCENE_VERB: Record<ProviderErrorScene, string> = {
  save: '保存',
  test: '测试',
  activate: '启用',
  deactivate: '停用',
  register: '登记',
  'endpoint-state': '操作',
  material: '写入',
};

export function describeProviderError(
  error: unknown,
  scene: ProviderErrorScene,
  ctx: ProviderErrorContext = {},
): ProviderNotice {
  if (isAmbiguousWriteError(error)) {
    return {
      kind: 'generic',
      tone: 'warn',
      title: '请求结果未知',
      body: '服务端可能已经生效，已刷新列表。请先确认最新状态，未生效时再重试。',
      code: error instanceof AdminApiError ? error.code : '',
      placement: 'toast',
      refresh: true,
    };
  }
  if (!(error instanceof AdminApiError)) {
    return {
      kind: 'generic',
      tone: 'neg',
      title: '操作失败，请重试',
      code: '',
      placement: 'toast',
    };
  }

  const details = (error.details ?? {}) as Record<string, unknown>;
  const appName = (id: string) => ctx.appName?.(id) ?? id;
  const parsedChannel = PaymentProviderChannelSchema.safeParse(details.channel);
  const channel = ctx.channel ?? (parsedChannel.success ? parsedChannel.data : null);
  const provider = providerDisplayName(channel);
  const copy: ProviderChannelCopy | null = channel ? providerChannelCopy(channel) : null;
  const materialName = copy?.materialName ?? '验签材料';
  const consoleName = copy?.consoleName ?? '供应商后台';

  switch (error.code) {
    case 'PAY_PROVIDER_NOT_CONFIGURED': {
      const appId = stringDetail(details, 'app_id');
      if (detailsReasonIs(details, 'CREDENTIAL_ROUTE_MISSING')) {
        if (scene === 'material' && appId) {
          return {
            kind: 'route-missing',
            tone: 'neg',
            title: '本次轮转没有生效',
            body: `${appName(appId)} 的凭据在本环境不可用，无法判断它是否受影响。请先重新提交 ${appName(appId)} 的整组凭据，再轮转。`,
            code: 'PAY_PROVIDER_NOT_CONFIGURED · CREDENTIAL_ROUTE_MISSING',
            placement: 'inline',
            appId,
          };
        }
        return {
          kind: 'route-missing',
          tone: 'neg',
          title: '当前凭据在本环境不可用',
          body: '请重新提交整组凭据。',
          code: 'PAY_PROVIDER_NOT_CONFIGURED · CREDENTIAL_ROUTE_MISSING',
          placement: 'inline',
          appId,
        };
      }
      if (scene === 'register') {
        return {
          kind: 'generic',
          tone: 'neg',
          title: '这个商户号 + mode 下没有任何 App 配置',
          body: '先建配置再登记回调。已刷新列表。',
          code: 'PAY_PROVIDER_NOT_CONFIGURED · CONFIG_MISSING',
          placement: 'toast',
          refresh: true,
        };
      }
      return {
        kind: 'generic',
        tone: 'neg',
        title: '这个 App 的配置不存在',
        body: '可能刚被删除。已刷新列表，请重新新增。',
        code: 'PAY_PROVIDER_NOT_CONFIGURED · CONFIG_MISSING',
        placement: 'toast',
        refresh: true,
        appId,
      };
    }

    case 'PAY_PROVIDER_NOT_READY': {
      const codes = issueCodes(details);
      const code = `PAY_PROVIDER_NOT_READY · ${codes.join(' / ') || '—'}`;
      if (codes.includes('CREDENTIAL_STORE_UNAVAILABLE')) {
        return {
          kind: 'generic',
          tone: 'info',
          title: '环境级问题，需要运维处理',
          body: '本环境没有接入凭据加密载体，保存、测试、启用与写入材料都会失败。重新填写无法解决。',
          code,
          placement: 'inline',
          environmental: true,
        };
      }
      if (codes.includes('MODE_MISMATCH')) {
        const mode = ctx.deploymentMode;
        if (mode === null) {
          return {
            kind: 'generic',
            tone: 'info',
            title: '本环境未声明 mode，需要运维处理',
            body: '任何 mode 都会被拒。在页面上重填没有用，请联系运维。',
            code,
            placement: 'inline',
            environmental: true,
          };
        }
        return {
          kind: 'generic',
          tone: 'warn',
          title: 'mode 与本环境不一致',
          body: mode
            ? `本环境为 ${mode}，请把配置改成 ${mode} 后保存并重新测试。`
            : '请把配置改成本环境对应的 mode 后保存并重新测试；本环境 mode 暂未读到，可刷新能力声明后查看。',
          code,
          placement: 'inline',
        };
      }
      if (codes.includes('VERIFICATION_MATERIAL_MISSING')) {
        return {
          kind: 'material-missing',
          tone: 'warn',
          title: `${SCENE_VERB[scene]}未执行：该商户组还没有验签材料`,
          body: `先登记回调地址并填入${consoleName}，再写入${materialName}作为验签材料，然后重新测试。连接状态没有改变。`,
          code,
          placement: 'inline',
        };
      }
      if (codes.includes('CONFIG_INCOMPLETE')) {
        return {
          kind: 'config-incomplete',
          tone: 'warn',
          title: '原生回跳缺失或 host 已不在登记中',
          body: '去编辑补正原生回跳后再测试。',
          code,
          placement: 'inline',
        };
      }
      if (codes.includes('CONNECTION_UNVERIFIED')) {
        return {
          kind: 'connection-unverified',
          tone: 'warn',
          title: '还没有测试通过',
          body: '配置保存后回到了待验证，先测试连接再启用。',
          code,
          placement: 'inline',
          refresh: true,
        };
      }
      return { kind: 'generic', tone: 'neg', title: error.message, code, placement: 'inline' };
    }

    case 'PAY_PROVIDER_CONNECTION_FAILED':
      return detailsReasonIs(details, 'PERMISSION_DENIED')
        ? {
            kind: 'generic',
            tone: 'neg',
            title: `${provider} 拒绝了访问权限`,
            body: `商户账户未授予所需权限。请在${consoleName}核对后重新测试。状态已变为「已失效」。`,
            code: 'PAY_PROVIDER_CONNECTION_FAILED · PERMISSION_DENIED',
            placement: 'toast',
            refresh: true,
          }
        : {
            kind: 'generic',
            tone: 'neg',
            title: `${provider} 拒绝了这组凭据`,
            body: `${copy?.credentialRejectedBody ?? '凭据有误，或填的是另一环境的凭据。'}状态已变为「已失效」。`,
            code: 'PAY_PROVIDER_CONNECTION_FAILED · CREDENTIAL_INVALID',
            placement: 'toast',
            refresh: true,
          };

    case 'SERVICE_UNAVAILABLE':
      if (scene === 'register') {
        return {
          kind: 'generic',
          tone: 'warn',
          title: '回调地址暂时无法生成',
          body: '本环境回调域未配置或服务暂不可用；多次失败请联系运维。',
          code: 'SERVICE_UNAVAILABLE',
          placement: 'toast',
        };
      }
      return {
        kind: 'generic',
        tone: 'warn',
        title: scene === 'test' ? `${provider} 暂时不可用，状态没有改变` : '服务暂时不可用',
        body: '稍后重试。',
        code: 'SERVICE_UNAVAILABLE',
        placement: 'toast',
      };

    case 'RESOURCE_CONFLICT':
      if (detailsReasonIs(details, 'CONFIG_VERSION_CHANGED')) {
        return {
          kind: 'generic',
          tone: 'neg',
          title: '配置在操作期间被他人修改',
          body:
            scene === 'test'
              ? '已刷新。本次测试结果没有写入，请重新测试。'
              : '已刷新，请确认最新配置后重试。',
          code: 'RESOURCE_CONFLICT · CONFIG_VERSION_CHANGED',
          placement: 'toast',
          refresh: true,
        };
      }
      if (detailsReasonIs(details, 'IDEMPOTENCY_KEY_MISMATCH')) {
        return {
          kind: 'generic',
          tone: 'neg',
          title: '提交内容与上一次请求不一致',
          body: '已换用新的请求标识，请重新提交。',
          code: 'RESOURCE_CONFLICT · IDEMPOTENCY_KEY_MISMATCH',
          placement: 'toast',
          rotateIdempotencyKey: true,
        };
      }
      if (detailsReasonIs(details, 'OPERATION_IN_PROGRESS')) {
        return {
          kind: 'generic',
          tone: 'warn',
          title: '同一请求正在处理',
          body: '请稍候再刷新，不要重复提交。',
          code: 'RESOURCE_CONFLICT · OPERATION_IN_PROGRESS',
          placement: 'toast',
        };
      }
      return {
        kind: 'generic',
        tone: 'neg',
        title: error.message,
        code: 'RESOURCE_CONFLICT',
        placement: 'toast',
        refresh: true,
      };

    case 'RESOURCE_NOT_FOUND':
      return scene === 'endpoint-state' || scene === 'material'
        ? {
            kind: 'generic',
            tone: 'neg',
            title: '回调地址不存在或已被他人退役',
            body: '已刷新列表。',
            code: 'RESOURCE_NOT_FOUND',
            placement: 'toast',
            refresh: true,
          }
        : {
            kind: 'generic',
            tone: 'neg',
            title: '对象不存在或已被删除',
            body: '已刷新列表。',
            code: 'RESOURCE_NOT_FOUND',
            placement: 'toast',
            refresh: true,
          };

    case 'VALIDATION_FAILED': {
      const fields = (error.errors ?? []).map((e) => e.field);
      if (fields.includes('material')) {
        return {
          kind: 'generic',
          tone: 'neg',
          title: copy?.materialInvalidTitle ?? '验签材料格式不正确',
          body: copy?.materialInvalidBody,
          code: 'VALIDATION_FAILED · material',
          placement: 'inline',
        };
      }
      if (fields.includes('appId')) {
        return {
          kind: 'generic',
          tone: 'neg',
          title: `这个 App 的平台不支持 ${provider}`,
          code: 'VALIDATION_FAILED · appId',
          placement: 'inline',
        };
      }
      return {
        kind: 'generic',
        tone: 'neg',
        title: error.message,
        code: `VALIDATION_FAILED${fields.length ? ` · ${fields.join(' / ')}` : ''}`,
        placement: 'inline',
      };
    }

    case 'FORBIDDEN_OPERATION':
      return {
        kind: 'generic',
        tone: 'neg',
        title: '需要 admin 权限并完成 MFA',
        code: 'FORBIDDEN_OPERATION',
        placement: 'toast',
      };

    default:
      if (error.code.startsWith('AUTH_')) {
        return {
          kind: 'generic',
          tone: 'neg',
          title: '需要 admin 权限并完成 MFA',
          code: error.code,
          placement: 'toast',
        };
      }
      return {
        kind: 'generic',
        tone: 'neg',
        title: error.message || '操作失败，请重试',
        code: error.code,
        placement: 'toast',
      };
  }
}

/** toast 单行文案：标题 + 说明。 */
export function noticeToastText(notice: ProviderNotice): string {
  return notice.body ? `${notice.title}：${notice.body}` : notice.title;
}

/** toast 色调：与行内提示块同一套语义。 */
export function noticeToastTone(notice: ProviderNotice): 'neg' | 'warn' | 'info' {
  return notice.tone;
}
